import { useState, useMemo, useEffect } from 'react';
import { Calendar, RefreshCw, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  computeOccurrences,
  formatWeeksOfMonth,
  patternToRRule,
  resolveWeeksOfMonth,
} from '@littlebooker/dal';
import type { RecurrencePattern, DayOfWeek, WeekOfMonth } from '@littlebooker/dal';

const DAYS_OF_WEEK: { value: DayOfWeek; label: string; short: string }[] = [
  { value: 'MO', label: 'Monday', short: 'Mon' },
  { value: 'TU', label: 'Tuesday', short: 'Tue' },
  { value: 'WE', label: 'Wednesday', short: 'Wed' },
  { value: 'TH', label: 'Thursday', short: 'Thu' },
  { value: 'FR', label: 'Friday', short: 'Fri' },
  { value: 'SA', label: 'Saturday', short: 'Sat' },
  { value: 'SU', label: 'Sunday', short: 'Sun' },
];

const WEEKS_OF_MONTH: { value: WeekOfMonth; label: string; ordinal: string }[] = [
  { value: 1, label: 'First', ordinal: '1st' },
  { value: 2, label: 'Second', ordinal: '2nd' },
  { value: 3, label: 'Third', ordinal: '3rd' },
  { value: 4, label: 'Fourth', ordinal: '4th' },
  { value: -1, label: 'Last', ordinal: 'Last' },
];

// Common presets for quick selection
const PRESETS: { label: string; pattern: RecurrencePattern }[] = [
  { label: 'Every Monday', pattern: { frequency: 'weekly', dayOfWeek: 'MO' } },
  { label: 'Every Tuesday', pattern: { frequency: 'weekly', dayOfWeek: 'TU' } },
  { label: 'Every Wednesday', pattern: { frequency: 'weekly', dayOfWeek: 'WE' } },
  { label: 'Every Thursday', pattern: { frequency: 'weekly', dayOfWeek: 'TH' } },
  { label: 'Every Friday', pattern: { frequency: 'weekly', dayOfWeek: 'FR' } },
  { label: 'Every Saturday', pattern: { frequency: 'weekly', dayOfWeek: 'SA' } },
  { label: 'Every Sunday', pattern: { frequency: 'weekly', dayOfWeek: 'SU' } },
  { label: '1st Monday of month', pattern: { frequency: 'monthly', weekOfMonth: 1, dayOfWeek: 'MO' } },
  { label: '1st Wednesday of month', pattern: { frequency: 'monthly', weekOfMonth: 1, dayOfWeek: 'WE' } },
  { label: '1st Friday of month', pattern: { frequency: 'monthly', weekOfMonth: 1, dayOfWeek: 'FR' } },
  { label: '2nd Saturday of month', pattern: { frequency: 'monthly', weekOfMonth: 2, dayOfWeek: 'SA' } },
  { label: 'Last Friday of month', pattern: { frequency: 'monthly', weekOfMonth: -1, dayOfWeek: 'FR' } },
  { label: '2nd, 4th & last Wednesday', pattern: { frequency: 'monthly', weeksOfMonth: [2, 4, -1], dayOfWeek: 'WE' } },
  { label: '1st Monday every 2 months', pattern: { frequency: 'bi-monthly', weekOfMonth: 1, dayOfWeek: 'MO' } },
  { label: '1st Friday every 2 months', pattern: { frequency: 'bi-monthly', weekOfMonth: 1, dayOfWeek: 'FR' } },
  { label: 'Last Friday every 2 months', pattern: { frequency: 'bi-monthly', weekOfMonth: -1, dayOfWeek: 'FR' } },
];

export type RecurrencePreset = {
  name: string;
  pattern: RecurrencePattern;
};

interface RecurrencePatternPickerProps {
  pattern: RecurrencePattern | null;
  onChange: (pattern: RecurrencePattern) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  organizationPresets?: RecurrencePreset[];
  className?: string;
}

// Preview dates come from the DAL so the preview can never diverge from what
// generation actually produces (this used to be a second implementation).
function computePreviewDates(
  pattern: RecurrencePattern | null,
  startDate: string,
  endDate: string,
  startTime: string,
  maxCount: number = 5
): Date[] {
  if (!pattern || !startDate || !endDate) return [];
  try {
    const time = startTime ? `${startTime}:00` : '00:00:00';
    return computeOccurrences(patternToRRule(pattern), startDate, endDate, time, 'UTC').slice(0, maxCount);
  } catch {
    // An incomplete custom pattern (e.g. no week selected yet) simply has no preview.
    return [];
  }
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function patternsMatch(a: RecurrencePattern | null, b: RecurrencePattern): boolean {
  if (!a) return false;
  if (a.frequency !== b.frequency) return false;
  if (a.dayOfWeek !== b.dayOfWeek) return false;
  if ((a.frequency === 'monthly' || a.frequency === 'bi-monthly') &&
    (b.frequency === 'monthly' || b.frequency === 'bi-monthly')) {
    // Normalise both shapes so a legacy preset ({weekOfMonth: 2}) still matches a
    // picker-emitted pattern ({weeksOfMonth: [2]}).
    const wa = resolveWeeksOfMonth(a);
    const wb = resolveWeeksOfMonth(b);
    return a.frequency === b.frequency && wa.length === wb.length && wa.every((w, i) => w === wb[i]);
  }
  return true;
}

export function RecurrencePatternPicker({
  pattern,
  onChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startTime,
  onStartTimeChange,
  organizationPresets,
  className,
}: RecurrencePatternPickerProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [frequency, setFrequency] = useState<'weekly' | 'monthly' | 'bi-monthly'>(
    pattern?.frequency || 'weekly'
  );
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>(
    pattern?.dayOfWeek || 'WE'
  );
  const [weeksOfMonth, setWeeksOfMonth] = useState<WeekOfMonth[]>(
    pattern?.frequency === 'monthly' || pattern?.frequency === 'bi-monthly'
      ? resolveWeeksOfMonth(pattern)
      : [1]
  );

  // Organization presets come first, then the built-ins they do not already cover.
  // Merging rather than replacing matters: an org with its own presets used to lose
  // access to every built-in pattern, including '2nd, 4th & last Wednesday'.
  const presetsToUse = useMemo(() => {
    const orgPresets = (organizationPresets ?? []).map(p => ({
      label: p.name,
      pattern: p.pattern,
    }));
    const builtIns = PRESETS.filter(
      builtIn => !orgPresets.some(orgPreset => patternsMatch(orgPreset.pattern, builtIn.pattern))
    );
    return [...orgPresets, ...builtIns];
  }, [organizationPresets]);

  // Check if current pattern matches any preset
  const matchingPreset = useMemo(() => {
    return presetsToUse.find(p => patternsMatch(pattern, p.pattern));
  }, [pattern, presetsToUse]);

  // Show custom picker if no preset matches
  useEffect(() => {
    if (pattern && !matchingPreset) {
      setShowCustom(true);
    }
  }, [pattern, matchingPreset]);

  // Update pattern when custom selections change
  const updatePattern = (freq: 'weekly' | 'monthly' | 'bi-monthly', day: DayOfWeek, weeks: WeekOfMonth[]) => {
    if (freq === 'weekly') {
      onChange({ frequency: 'weekly', dayOfWeek: day });
    } else {
      onChange({ frequency: freq, weeksOfMonth: weeks, dayOfWeek: day });
    }
  };

  const handleFrequencyChange = (newFreq: 'weekly' | 'monthly' | 'bi-monthly') => {
    setFrequency(newFreq);
    updatePattern(newFreq, dayOfWeek, weeksOfMonth);
  };

  const handleDayChange = (newDay: DayOfWeek) => {
    setDayOfWeek(newDay);
    updatePattern(frequency, newDay, weeksOfMonth);
  };

  // Toggling is additive: pick as many weeks as you like (e.g. 2nd + 4th + Last).
  // Deselecting the only remaining week is a no-op — a monthly pattern needs one.
  const handleWeekToggle = (week: WeekOfMonth) => {
    const next = weeksOfMonth.includes(week)
      ? weeksOfMonth.filter((w) => w !== week)
      : [...weeksOfMonth, week];
    if (next.length === 0) return;
    const ordered = WEEKS_OF_MONTH.map((w) => w.value).filter((w) => next.includes(w));
    setWeeksOfMonth(ordered);
    updatePattern(frequency, dayOfWeek, ordered);
  };

  const handlePresetSelect = (preset: typeof PRESETS[0]) => {
    setShowCustom(false);
    setFrequency(preset.pattern.frequency);
    setDayOfWeek(preset.pattern.dayOfWeek);
    if (preset.pattern.frequency === 'monthly' || preset.pattern.frequency === 'bi-monthly') {
      setWeeksOfMonth(resolveWeeksOfMonth(preset.pattern));
    }
    onChange(preset.pattern);
  };

  // Compute preview dates
  const previewDates = useMemo(() => {
    return computePreviewDates(pattern, startDate, endDate, startTime);
  }, [pattern, startDate, endDate, startTime]);

  // Generate human-readable pattern description
  const patternDescription = useMemo(() => {
    if (!pattern) return '';
    if (pattern.frequency === 'weekly') {
      const day = DAYS_OF_WEEK.find(d => d.value === pattern.dayOfWeek);
      return `Every ${day?.label || pattern.dayOfWeek}`;
    }
    const weeks = formatWeeksOfMonth(resolveWeeksOfMonth(pattern));
    const day = DAYS_OF_WEEK.find(d => d.value === pattern.dayOfWeek);
    if (!weeks) return '';
    return pattern.frequency === 'monthly'
      ? `Every ${weeks} ${day?.label || ''} of the month`
      : `Every ${weeks} ${day?.label || ''} every 2 months`;
  }, [pattern]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Pattern Summary - Prominent at top */}
      {pattern && (
        <div className="bg-info-muted border border-info-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-info-foreground">
            <RefreshCw className="h-5 w-5" />
            <span className="text-lg font-semibold">{patternDescription}</span>
          </div>
          {startTime && (
            <p className="text-sm text-info-foreground/80 mt-1">at {startTime}</p>
          )}
        </div>
      )}

      {/* Quick Presets */}
      <div>
        <label className="block text-sm font-medium text-card-foreground mb-2">
          <Sparkles className="h-4 w-4 inline mr-1" />
          Quick select
        </label>
        {/* One grid over the whole merged list. This was two hardcoded slices, which
            silently hid whichever preset landed on the boundary index. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {presetsToUse.map((preset, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              className={cn(
                'px-3 py-2 rounded-md border text-sm font-medium transition-colors text-left',
                patternsMatch(pattern, preset.pattern)
                  ? 'bg-info text-info-foreground border-info'
                  : 'bg-card text-card-foreground border-border hover:bg-muted/30 hover:border-info-border'
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Configuration Toggle */}
      <button
        type="button"
        onClick={() => setShowCustom(!showCustom)}
        className="text-sm text-info hover:text-info/80 font-medium"
      >
        {showCustom
          ? '− Hide custom options'
          : '+ Custom pattern (combine weeks, e.g. 2nd + 4th + last)...'}
      </button>

      {/* Custom Configuration */}
      {showCustom && (
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground mb-3">
            Build your own recurrence pattern:
          </p>

          {/* Frequency toggle */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => handleFrequencyChange('weekly')}
              className={cn(
                'flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors',
                frequency === 'weekly'
                  ? 'bg-info text-info-foreground border-info'
                  : 'bg-card text-card-foreground border-border hover:bg-muted/30'
              )}
            >
              Weekly
            </button>
            <button
              type="button"
              onClick={() => handleFrequencyChange('monthly')}
              className={cn(
                'flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors',
                frequency === 'monthly'
                  ? 'bg-info text-info-foreground border-info'
                  : 'bg-card text-card-foreground border-border hover:bg-muted/30'
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => handleFrequencyChange('bi-monthly')}
              className={cn(
                'flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors',
                frequency === 'bi-monthly'
                  ? 'bg-info text-info-foreground border-info'
                  : 'bg-card text-card-foreground border-border hover:bg-muted/30'
              )}
            >
              Bi-monthly
            </button>
          </div>

          {/* Natural language builder */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">Every</span>

            {(frequency === 'monthly' || frequency === 'bi-monthly') && (
              <span className="inline-flex gap-1" role="group" aria-label="Weeks of the month">
                {WEEKS_OF_MONTH.map((week) => {
                  const selected = weeksOfMonth.includes(week.value);
                  return (
                    <button
                      key={week.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => handleWeekToggle(week.value)}
                      className={cn(
                        'px-3 py-1.5 rounded-md border text-sm font-medium transition-colors',
                        selected
                          ? 'bg-info text-info-foreground border-info'
                          : 'bg-card text-card-foreground border-border hover:bg-muted/30'
                      )}
                    >
                      {week.ordinal}
                    </button>
                  );
                })}
              </span>
            )}

            <select
              value={dayOfWeek}
              onChange={(e) => handleDayChange(e.target.value as DayOfWeek)}
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm font-medium"
            >
              {DAYS_OF_WEEK.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>

            {frequency === 'monthly' && (
              <span className="font-medium">of the month</span>
            )}
            {frequency === 'bi-monthly' && (
              <span className="font-medium">every 2 months</span>
            )}
          </div>

          {(frequency === 'monthly' || frequency === 'bi-monthly') && (
            <p className="text-xs text-muted-foreground">
              Pick as many weeks as you need — 2nd, 4th and Last together gives three
              shows a month. In a month where the 4th is also the last, they collapse
              into one.
            </p>
          )}

        </div>
      )}

      {/* Date Range */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Start date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-info focus:border-info bg-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            End date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={startDate}
            className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-info focus:border-info bg-input"
          />
        </div>
      </div>

      {/* Start Time */}
      <div>
        <label className="block text-sm font-medium text-card-foreground mb-1">
          Start time
        </label>
        <input
          type="time"
          value={startTime}
          onChange={(e) => onStartTimeChange(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-info focus:border-info bg-input"
        />
      </div>

      {/* Preview */}
      {previewDates.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            Preview (next {previewDates.length} occurrences)
          </label>
          <div className="bg-muted/30 rounded-md p-3 space-y-1">
            {previewDates.map((date, idx) => (
              <div key={idx} className="text-sm text-muted-foreground">
                {formatDate(date)}
              </div>
            ))}
            {endDate && (
              <div className="text-xs text-muted-foreground/70 mt-2 pt-2 border-t border-border">
                Series ends: {formatDate(new Date(endDate))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export type { RecurrencePattern, DayOfWeek, WeekOfMonth };
